import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Edit, Trash2 } from 'lucide-react';
import LessonForm from '../forms/lesson-form';

export default function LessonsTab({ selectedCourse, allLessons, setAllLessons }) {
  const [editingLesson, setEditingLesson] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Filter lessons based on the selected course
  const lessons = allLessons.filter((l) => l.courseId === selectedCourse.id);

  const handleAddLesson = () => {
    setEditingLesson(null);
    setShowForm(true);
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setShowForm(true);
  };

  const handleDeleteLesson = (id) => {
    setAllLessons(allLessons.filter((l) => l.id !== id));
  };

  const handleSaveLesson = (newLesson) => {
    if (newLesson.id) {
      setAllLessons(allLessons.map((l) => (l.id === newLesson.id ? newLesson : l)));
    } else {
      setAllLessons([...allLessons, { ...newLesson, id: `l${allLessons.length + 1}`, courseId: selectedCourse.id }]);
    }
    setShowForm(false);
  };

  const handleCancelForm = () => {
    setShowForm(false);
  };

  const cardClass = 'bg-white rounded-lg shadow p-6';
  const cardHeaderClass = 'flex flex-row items-center justify-between mb-4';
  const cardTitleClass = 'text-2xl font-bold text-gray-800';
  const buttonClass = 'px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 flex items-center';
  const tableClass = 'min-w-full divide-y divide-gray-200';
  const tableHeaderClass = 'bg-gray-900';
  const tableHeadClass = 'px-6 py-3 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider';
  const tableCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-gray-900';
  const actionButtonClass = 'p-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500';

  if (showForm) {
    return (
      <LessonForm
        lesson={editingLesson}
        onSave={handleSaveLesson}
        onCancel={handleCancelForm}
        courseId={selectedCourse.id}
      />
    );
  }

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h2 className={cardTitleClass}>
          Lessons for
          {selectedCourse.name}
        </h2>
        <button type="button" onClick={handleAddLesson} className={buttonClass}>
          <Plus className="mr-2 h-4 w-4" />
          {' '}
          Add New Lesson
        </button>
      </div>
      <div>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead className={tableHeaderClass}>
              <tr>
                <th scope="col" className={tableHeadClass}>Name</th>
                <th scope="col" className={tableHeadClass}>Description</th>
                <th scope="col" className={tableHeadClass}>Loom Video URL</th>
                <th scope="col" className={`${tableHeadClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lessons.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No lessons for this course yet.</td>
                </tr>
              ) : (
                lessons.map((lesson, index) => (
                  <tr
                    key={lesson.id}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}
                  >
                    <td className={`${tableCellClass} font-medium`}>{lesson.name}</td>
                    <td className={tableCellClass}>{lesson.description}</td>
                    <td className={tableCellClass}>
                      {lesson.loomVideoUrl ? (
                        <a
                          href={lesson.loomVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View Video
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className={`${tableCellClass} text-right`}>
                      <button type="button" className={actionButtonClass} onClick={() => handleEditLesson(lesson)}>
                        <Edit className="h-4 w-4 text-gray-600" />
                        <span className="sr-only">Edit</span>
                      </button>
                      <button
                        type="button"
                        className={actionButtonClass}
                        onClick={() => handleDeleteLesson(lesson.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                        <span className="sr-only">Delete</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
LessonsTab.propTypes = {
  selectedCourse: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  allLessons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      courseId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      loomVideoUrl: PropTypes.string,
    }),
  ).isRequired,
  setAllLessons: PropTypes.func.isRequired,
};
